import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'

import { RequestStatus } from 'clickNgo/configs/service/common'

import { initialState } from './initialState'
import { P360_CONTACTS_NAMESPACE } from './model/constants'
import {
  createContact,
  deleteContact,
  fetchContacts,
  updateContact,
  uploadContactImage,
} from './thunks'

const contactsSlice = createSlice({
  name: P360_CONTACTS_NAMESPACE,
  initialState,
  reducers: {
    setFilters(
      state,
      action: PayloadAction<{ partnerId: string; programType: ProgramTypes | null }>
    ) {
      state.partnerId = action.payload.partnerId
      state.programType = action.payload.programType
    },
    clearContacts(state) {
      state.data = []
      state.error = null
      state.status = RequestStatus.NONE
    },
  },
  extraReducers(builder) {
    builder
      // Fetch contacts
      .addCase(fetchContacts.pending, (state) => {
        state.isLoading = true
        state.status = RequestStatus.PENDING
        state.error = null
        state.data = []
      })
      .addCase(fetchContacts.rejected, (state, action) => {
        state.isLoading = false
        state.status = RequestStatus.REJECTED
        state.error = action.error.message || 'Failed to fetch contacts'
      })
      .addCase(fetchContacts.fulfilled, (state, action) => {
        state.isLoading = false
        state.status = RequestStatus.FULFILLED
        state.data = action.payload
      })

      // Create contact
      .addCase(createContact.pending, (state) => {
        state.isLoading = true
      })
      .addCase(createContact.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to create contact'
      })
      .addCase(createContact.fulfilled, (state, action) => {
        state.isLoading = false
        const newContact = action.payload
        state.data.unshift(newContact)
      })

      // Update contact
      .addCase(updateContact.pending, (state) => {
        state.isLoading = true
      })
      .addCase(updateContact.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to update contact'
      })
      .addCase(updateContact.fulfilled, (state, action) => {
        state.isLoading = false
        const updatedContact = action.payload
        const index = state.data.findIndex((c) => c.EntryId === updatedContact.EntryId)
        if (index !== -1) {
          state.data[index] = updatedContact
        }
      })

      // Delete contact
      .addCase(deleteContact.pending, (state) => {
        state.isLoading = true
      })
      .addCase(deleteContact.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to delete contact'
      })
      .addCase(deleteContact.fulfilled, (state, action) => {
        state.isLoading = false
        const deletedEntryId = action.payload
        const index = state.data.findIndex((c) => c.EntryId === deletedEntryId)
        if (index !== -1) {
          state.data.splice(index, 1)
        }
      })

      // Upload contact image
      .addCase(uploadContactImage.pending, (state) => {
        state.isLoading = true
      })
      .addCase(uploadContactImage.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to upload contact image'
      })
      .addCase(uploadContactImage.fulfilled, (state, action) => {
        state.isLoading = false
        const updatedContact = action.payload
        const contactIndex = state.data.findIndex((c) => c.EntryId === updatedContact.EntryId)
        if (contactIndex !== -1) {
          state.data[contactIndex] = updatedContact
        }
      })
  },
})

export default contactsSlice.reducer

export const { setFilters, clearContacts } = contactsSlice.actions
